import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signInAnonymously,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';
import { isUserAdmin, createAdminUserDocument } from '../utils/adminUtils';
import { createUserProfile, getUserProfile, getApprovedCoachUids } from '../utils/firebaseUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [approvedCoachUids, setApprovedCoachUids] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sign up with email and password
  const signup = (email, password, profileData = null) => {
    return createUserWithEmailAndPassword(auth, email, password).then(async (result) => {
      if (profileData) {
        await createUserProfile(result.user.uid, profileData);
      }
      return result;
    });
  };

  // Sign in with email and password
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sign in with Google
  const loginWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider).then(async (result) => {
      if (result._tokenResponse?.isNewUser) {
        console.log('New Google user signed up');
      }
      return result;
    });
  };

  // Sign in anonymously
  const loginAnonymously = () => {
    return signInAnonymously(auth);
  };

  // Sign out
  const logout = () => {
    return signOut(auth);
  };

  // Check if user can make reservations
  const canMakeReservations = () => {
    return currentUser && !currentUser.isAnonymous;
  };

  useEffect(() => {
    const loadApprovedCoaches = async () => {
      try {
        const uids = await getApprovedCoachUids();
        setApprovedCoachUids(uids);
      } catch (err) {
        console.error('Error loading approved coaches:', err);
        setApprovedCoachUids([]);
      }
    };
    loadApprovedCoaches();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Create admin user document if user is admin
        if (isUserAdmin(user)) {
          try {
            await createAdminUserDocument(user.uid, {
              email: user.email,
              displayName: user.displayName || 'TenisoUžsiėmimai.lt',
              photoURL: user.photoURL
            });
          } catch (error) {
            console.error('Error creating admin user document:', error);
          }
        }
        
        // Load user profile
        try {
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);
        } catch (error) {
          console.error('Error loading user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    approvedCoachUids,
    signup,
    login,
    loginWithGoogle,
    loginAnonymously,
    logout,
    canMakeReservations,
    setUserProfile,
    refreshApprovedCoaches: async () => {
      try {
        const uids = await getApprovedCoachUids();
        setApprovedCoachUids(uids);
      } catch (err) {
        console.error('Error refreshing approved coaches:', err);
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 